"use client";

import { Modal } from "@wojo/ui";
import { MyAccountBasicMyTripFragment, TermFragment } from "generated/graphql";
import React, { useEffect, useId, useRef, useState } from "react";
import { CloseGate } from "./CloseGate";
import { ReviewTermsForm } from "./ReviewTermsForm";
import { SuccessScreen } from "./SuccessScreen";

export type TermsAndConditionsModalProps = {
    /**
     * Where to redirect the user if they try to close the modal without accepting the terms
     */
    closeModalRedirectLink?: string;
    /**
     * Can the modal be fully closed by the user
     */
    isClosable?: boolean;
    /**
     * Is the modal open
     */
    isOpen: boolean;
    /**
     * Handle when the user tries to close the modal
     */
    onCloseRequested?: () => void;
    /**
     * Where to redirect the user after accepting the terms
     */
    successScreenRedirectLink?: string;
    /**
     * Terms that the user is accepting
     */
    term?: TermFragment | null; // TODO: Make required & non nullable https://ef-wojo.atlassian.net/browse/WWB-572
    /**
     * Trip that the user is accepting the terms for
     */
    trip: MyAccountBasicMyTripFragment;
};

export const TermsAndConditionsModal: React.FC<
    TermsAndConditionsModalProps
> = ({
    closeModalRedirectLink,
    isClosable = true,
    isOpen,
    onCloseRequested,
    successScreenRedirectLink,
    term,
    trip,
}) => {
    const [view, setView] = useState<"review" | "success" | "close-gate">(
        "review",
    );
    const titleId = useId();
    const modalRef = useRef<HTMLDivElement>(null);

    const closeModal = () => {
        if (view === "success" || view === "close-gate") {
            onCloseRequested?.();
            setView("review");
        } else {
            setView("close-gate");
        }
    };

    useEffect(() => {
        modalRef.current?.querySelector("dialog")?.focus();
    }, [view]);

    return (
        <Modal
            ref={modalRef}
            isOpen={isOpen}
            accessibleTitleId={titleId}
            onCloseRequested={closeModal}
            closeOnOutsideClick={isClosable || view !== "close-gate"}
            hideDefaultHeader={!isClosable && view === "close-gate"}
        >
            {/* TODO: Make term required & non nullable https://ef-wojo.atlassian.net/browse/WWB-572  */}
            {view === "review" && term && (
                <ReviewTermsForm
                    navigateToSuccess={() => setView("success")}
                    term={term}
                    titleId={titleId}
                    trip={trip}
                />
            )}
            {view === "success" && (
                <SuccessScreen
                    closeModal={closeModal}
                    redirectLink={successScreenRedirectLink}
                    titleId={titleId}
                />
            )}
            {view === "close-gate" && (
                <CloseGate
                    closeModal={closeModal}
                    redirectLink={closeModalRedirectLink}
                    returnToTerms={() => setView("review")}
                    titleId={titleId}
                />
            )}
        </Modal>
    );
};
